// Best-effort browser-side video compression using <video> + canvas.captureStream + MediaRecorder.
// Falls back to the original file when the browser doesn't support the required APIs.
export async function compressVideo(file, { maxDim = 720, targetBitrate = 1_200_000, onProgress } = {}) {
  if (!file || !file.type?.startsWith("video/")) return file;

  // Skip compression if browser lacks captureStream/MediaRecorder support
  const video = document.createElement("video");
  if (typeof HTMLCanvasElement.prototype.captureStream !== "function" ||
      typeof window.MediaRecorder === "undefined") {
    return file;
  }

  return new Promise((resolve) => {
    let audioStream = null;
    const url = URL.createObjectURL(file);
    video.muted = false;
    video.playsInline = true;
    video.src = url;
    let done = false;
    const finish = (result) => {
      if (done) return; done = true;
      try { URL.revokeObjectURL(url); } catch {}
      try { audioStream?.getTracks().forEach(t => t.stop()); } catch {}
      resolve(result || file);
    };

    video.onerror = () => finish(file);
    video.onloadedmetadata = () => {
      const scale = Math.min(1, maxDim / Math.max(video.videoWidth, video.videoHeight));
      const w = Math.max(2, Math.round(video.videoWidth * scale / 2) * 2);
      const h = Math.max(2, Math.round(video.videoHeight * scale / 2) * 2);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      const canvasStream = canvas.captureStream(30);

      // Try to grab audio via video.captureStream (Chromium) or fall back to video-only
      try {
        const vidStream = video.captureStream?.() || video.mozCaptureStream?.();
        vidStream?.getAudioTracks().forEach((t) => canvasStream.addTrack(t));
        audioStream = vidStream;
      } catch {}

      const preferred = [
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=vp9,opus",
        "video/webm",
      ];
      const mime = preferred.find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
      if (!mime) return finish(file);

      let rec;
      try {
        rec = new MediaRecorder(canvasStream, { mimeType: mime, videoBitsPerSecond: targetBitrate, audioBitsPerSecond: 96_000 });
      } catch { return finish(file); }
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        // Guard: if resulting size >= original, keep original
        if (blob.size >= file.size * 0.9) return finish(file);
        const compressed = new File([blob], file.name.replace(/\.[^.]+$/, ".webm"), { type: "video/webm" });
        finish(compressed);
      };

      const drawLoop = () => {
        if (video.paused || video.ended) return;
        try { ctx.drawImage(video, 0, 0, w, h); } catch {}
        if (onProgress && video.duration) onProgress(Math.min(1, video.currentTime / video.duration));
        requestAnimationFrame(drawLoop);
      };

      video.onended = () => { try { rec.stop(); } catch { finish(file); } };
      video.onplay = () => {
        try { rec.start(); } catch { return finish(file); }
        drawLoop();
      };
      video.play().catch(() => finish(file));

      // Safety timeout — if duration is unknown/very long, give up after 90s
      setTimeout(() => { if (!done) { try { rec.state === "recording" && rec.stop(); } catch {} } }, 90_000);
    };
  });
}
